
import { useCallback } from 'react';
import { TrainingSession, CompetitionMatch } from '../../types';
import { supabase } from '@/integrations/supabase/client';
import { formatStripeCount } from '../../utils/stripeCountUtils';
import { indexedDBService, offlineDB } from '@/services/offline/indexeddb';
import { useOfflineState } from '@/contexts/offline/useOfflineState';
import { STORES } from '@/services/offline/indexeddb/schema';
import type { TrainingSessionOffline } from '@/services/offline/indexeddb/schema';

// Define the database response type to match actual Supabase schema
interface DatabaseTrainingSession {
  id: string;
  user_id: string;
  title: string;
  date: string;
  duration: number;
  location: string;
  session_type: string;
  techniques_practiced: string[];
  notes: string;
  belt_rank: string;
  stripe_count: number;
  professor: string;
  learned: string;
  improvements: string;
  successes: string;
  youtube_url: string;
  competition_finish: string;
  competition_matches: CompetitionMatch[];
  created_at: string;
  updated_at: string;
}

// Helper function to convert offline session to TrainingSession
const convertOfflineToTrainingSession = (offlineSession: TrainingSessionOffline): TrainingSession => {
  // The offline schema has a simplified competition_matches structure
  // Convert it to the full CompetitionMatch format if possible
  const competitionMatches: CompetitionMatch[] = (offlineSession.competition_matches || []).map(match => {
    // Handle both old simplified format and new full format
    if ('gi_nogi' in match && 'weight_class' in match) {
      // Full format from database - type assertion for known structure
      const fullMatch = match as unknown as CompetitionMatch;
      return {
        id: undefined,
        gi_nogi: fullMatch.gi_nogi || 'gi',
        weight_class: fullMatch.weight_class || '',
        result: fullMatch.result || 'win',
        method: fullMatch.method || 'points',
        video_url: fullMatch.video_url,
        division: fullMatch.division || 'Adult'
      } as CompetitionMatch;
    } else {
      // Simplified format from offline schema - convert to full format
      return {
        id: undefined,
        gi_nogi: 'gi' as const,
        weight_class: '',
        result: (match.result === 'win' || match.result === 'loss') ? match.result as 'win' | 'loss' : 'win',
        method: 'points' as const,
        video_url: undefined,
        division: 'Adult' as const
      };
    }
  });

  const converted = {
    id: offlineSession.id,
    userId: offlineSession.user_id,
    title: offlineSession.title,
    date: offlineSession.date,
    duration: offlineSession.duration,
    professor: offlineSession.professor,
    techniques: offlineSession.techniques_practiced || [],
    location: offlineSession.location,
    notes: offlineSession.notes,
    learned: offlineSession.learned,
    improvements: offlineSession.improvements,
    successes: offlineSession.successes,
    youtubeUrl: offlineSession.youtube_url,
    beltRank: offlineSession.belt_rank,
    stripeCount: formatStripeCount(offlineSession.stripe_count),
    createdAt: offlineSession.created_at,
    updatedAt: offlineSession.updated_at,
    competitionTotalMatches: competitionMatches.length,
    competitionWeightClasses: '',
    competitionTotalWins: competitionMatches.filter(m => m.result === 'win').length,
    competitionFinish: offlineSession.competition_finish,
    competitionMatches: competitionMatches
  };

  return converted;
};

export const useSessionQueries = () => {
  const { state } = useOfflineState();

  const fetchSessions = useCallback(async (userId: string): Promise<TrainingSession[]> => {
    if (!userId) {
      throw new Error('User ID is required');
    }

    try {
      // Initialize IndexedDB if needed
      await indexedDBService.initialize();

      // Always try cache first for offline-first approach
      const cachedSessions = await indexedDBService.trainingSessions.getByUserId(userId);

      if (cachedSessions.length > 0) {
        // If offline, return cached data immediately
        if (!state.isOnline) {
          const convertedSessions = cachedSessions.map(convertOfflineToTrainingSession);

          // Match database sorting: date DESC, then created_at DESC (PostgreSQL default behavior)
          const sortedSessions = convertedSessions.sort((a, b) => {
            const dateA = new Date(a.date).getTime();
            const dateB = new Date(b.date).getTime();

            // Primary sort: by date (newest first)
            if (dateB !== dateA) {
              return dateB - dateA;
            }

            // Secondary sort: by created_at (newest first) - this matches PostgreSQL behavior
            const createdA = new Date(a.createdAt || '').getTime();
            const createdB = new Date(b.createdAt || '').getTime();
            return createdB - createdA;
          });

          return sortedSessions;
        }

        // If online, fetch fresh data from database instead of using cache
      }

      // If no cached data and we're offline, return empty array
      if (!state.isOnline) {
        return [];
      }

      // If online and no cache, fetch from database
      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', userId)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Database error fetching sessions:', error);
        throw new Error(`Failed to fetch training sessions: ${error.message}`);
      }

      // Convert database response to TrainingSession[] type
      const sessions = (data || []).map((dbSession: DatabaseTrainingSession) => {
        return {
          id: dbSession.id,
          userId: dbSession.user_id,
          title: dbSession.title,
          date: dbSession.date,
          duration: dbSession.duration,
          professor: dbSession.professor,
          techniques: dbSession.techniques_practiced || [],
          location: dbSession.location,
          notes: dbSession.notes,
          learned: dbSession.learned,
          improvements: dbSession.improvements,
          successes: dbSession.successes,
          youtubeUrl: dbSession.youtube_url,
          beltRank: dbSession.belt_rank,
          stripeCount: formatStripeCount(dbSession.stripe_count),
          createdAt: dbSession.created_at,
          updatedAt: dbSession.updated_at,
          competitionTotalMatches: dbSession.competition_matches ? dbSession.competition_matches.length : 0,
          competitionWeightClasses: '',
          competitionTotalWins: dbSession.competition_matches ? dbSession.competition_matches.filter((m: CompetitionMatch) => m.result === 'win').length : 0,
          competitionFinish: dbSession.competition_finish,
          competitionMatches: dbSession.competition_matches || []
        };
      });

      // Cache the fetched data in IndexedDB for offline use
      if (sessions.length > 0) {
        try {
          // Use the indexedDBService for consistency with the cache reading logic
          await Promise.all(
            data.map(async (dbSession: DatabaseTrainingSession) => {
              const offlineSession: TrainingSessionOffline = {
                id: dbSession.id,
                user_id: dbSession.user_id,
                title: dbSession.title,
                date: dbSession.date,
                duration: dbSession.duration,
                location: dbSession.location,
                session_type: dbSession.session_type,
                techniques_practiced: dbSession.techniques_practiced || [],
                notes: dbSession.notes,
                belt_rank: dbSession.belt_rank,
                stripe_count: dbSession.stripe_count,
                professor: dbSession.professor,
                learned: dbSession.learned,
                improvements: dbSession.improvements,
                successes: dbSession.successes,
                youtube_url: dbSession.youtube_url,
                competition_finish: dbSession.competition_finish,
                competition_matches: dbSession.competition_matches || [],
                created_at: dbSession.created_at,
                updated_at: dbSession.updated_at,
                _sync_status: 'synced' as const
              };

              return offlineDB.put(STORES.TRAINING_SESSIONS, offlineSession);
            })
          );
        } catch (cacheError) {
          console.error('❌ Failed to cache training sessions:', cacheError);
        }
      }

      return sessions;
    } catch (error) {
      console.error('Error in fetchSessions:', error);

      // Try to get cached data as fallback
      try {
        const cachedSessions = await indexedDBService.trainingSessions.getByUserId(userId);
        if (cachedSessions.length > 0) {
          const sortedSessions = cachedSessions
            .map(convertOfflineToTrainingSession)
            .sort((a, b) => {
              // Primary sort: by date (newest first)
              const dateA = new Date(a.date).getTime();
              const dateB = new Date(b.date).getTime();
              if (dateB !== dateA) {
                return dateB - dateA;
              }

              // Secondary sort: by created_at timestamp when dates are equal (newest first)
              const createdA = new Date(a.createdAt || '').getTime();
              const createdB = new Date(b.createdAt || '').getTime();
              return createdB - createdA;
            });
          return sortedSessions;
        }
      } catch (cacheError) {
        console.warn('Failed to get cached sessions as fallback:', cacheError);
      }

      throw error;
    }
  }, [state.isOnline]);

  return {
    fetchSessions,
  };
};
